import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Camera, Mail, Save, UserRound } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { uploadAvatar } from "@/lib/storage";
import { VerificationBadge } from "@/components/StatusBadge";
import { isInstalled, openInstallPrompt } from "@/components/InstallPrompt";
import type { VerificationStatus } from "@/lib/mock-data";

export const Route = createFileRoute("/_authenticated/customer/profile")({
  head: () => ({
    meta: [
      { title: "My Profile · Landoo" },
      { name: "description", content: "Manage your Landoo profile and personal details." },
    ],
  }),
  component: CustomerProfile,
});

function CustomerProfile() {
  const { profile, refreshProfile } = useAuth();
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [phone, setPhone] = useState(profile?.phone ?? "");
  const [city, setCity] = useState(profile?.city ?? "");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url ?? null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [installed, setInstalled] = useState(false);
  useEffect(() => setInstalled(isInstalled()), []);

  // The Supabase session hydrates after mount, so mirror it into the form once
  // the authenticated profile arrives (otherwise the page looks empty).
  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? "");
    setPhone(profile.phone ?? "");
    setCity(profile.city ?? "");
    setAvatarUrl(profile.avatar_url ?? null);
  }, [profile]);

  const onPickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    setUploading(true);
    try {
      const { url } = await uploadAvatar(profile.id, file);
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: url })
        .eq("id", profile.id);
      if (error) throw error;
      setAvatarUrl(url);
      toast.success("Photo updated");
      await refreshProfile();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!profile) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName, phone, city })
      .eq("id", profile.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Profile updated");
    await refreshProfile();
  };

  if (!profile) {
    return (
      <DashboardShell title="My Profile" role="Customer" items={CUSTOMER_NAV}>
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="h-64 animate-pulse rounded-2xl bg-secondary/60" />
          <div className="h-64 animate-pulse rounded-2xl bg-secondary/60" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="My Profile" role="Customer" items={CUSTOMER_NAV}>
      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <section className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-5 text-center shadow-soft">
          <div className="relative">
            <div className="grid h-28 w-28 place-items-center overflow-hidden rounded-full bg-cream ring-2 ring-border">
              {avatarUrl ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={avatarUrl}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserRound className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 inline-flex items-center gap-1 rounded-full bg-coral px-2.5 py-1.5 text-[10px] font-semibold text-white shadow-soft disabled:opacity-50"
            >
              <Camera className="h-3 w-3" /> {uploading ? "Uploading" : "Change"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickAvatar} />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-brown-dark">
              {fullName || "Your name"}
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Mail className="h-3 w-3" /> <span className="truncate">{profile?.email}</span>
            </div>
          </div>
          <VerificationBadge
            status={(profile?.verification_status as VerificationStatus) ?? "Not Started"}
          />
        </section>

        <section className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <h2 className="text-sm font-semibold text-brown-dark">Personal details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                Full name
              </div>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className={inp}
              />
            </label>
            <label className="block">
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                Email
              </div>
              <input value={profile?.email ?? ""} disabled className={`${inp} opacity-70`} />
            </label>
            <label className="block">
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                Mobile
              </div>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className={inp}
              />
            </label>
            <label className="block">
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                City
              </div>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Bengaluru"
                className={inp}
              />
            </label>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-coral px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </section>

        <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-soft lg:col-span-2">
          <h2 className="text-sm font-semibold text-brown-dark">Your activity</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {QUICK_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-semibold text-brown-dark transition hover:border-brand"
              >
                {label}
              </Link>
            ))}
          </div>
          <button
            type="button"
            onClick={openInstallPrompt}
            disabled={installed}
            className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs font-semibold text-brown-dark disabled:opacity-50"
          >
            {installed ? "App installed" : "Install Landoo app"}
          </button>
        </section>
      </div>
    </DashboardShell>
  );
}

const QUICK_LINKS = [
  { to: "/customer/favourites", label: "Favourites" },
  { to: "/customer/recently-viewed", label: "Recently viewed" },
  { to: "/customer/enquiries", label: "My enquiries" },
  { to: "/customer/settings", label: "Settings" },
] as const;

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";
